// Validation helpers for checking if sections are properly filled
// These functions replicate the validation logic from each form component

// Simplified validation helpers - return boolean only, no error objects
export const validateOrgDetails = (employeeData) => {
  // Basic validations
  if (!employeeData.empCode) return false;
  if (!employeeData.user?.name) return false;
  if (!employeeData.user?.dateOfBirth) return false;
  if (!employeeData.email) return false;
  if (!employeeData.user?.email) return false;
  if (!employeeData.user?.phone) return false;
  if (!employeeData.joiningDate) return false;
  
  // Required field validations
  if (!employeeData.branchId) return false;
  if (!employeeData.designationId) return false;
  if (!employeeData.departmentId) return false;
  if (!employeeData.subDepartmentId) return false;
  if (!employeeData.gradeId) return false;
  if (!employeeData.categoryId) return false;
  if (!employeeData.employmentType) return false;
  if (!employeeData.employmentStatus) return false;
  
  return true;
};

export const validatePersonalDetails = (employeeData) => {
  if (!employeeData.user?.name) return false;
  if (!employeeData.user?.gender) return false;
  if (!employeeData.user?.dateOfBirth) return false;
  if (!employeeData.user?.phone) return false;
  if (!employeeData.user?.email) return false;
  if (!employeeData.address?.address) return false;
  // nationality might not be in API response, so we'll skip it
  if (!employeeData.maritalStatus) return false;
  
  return true;
};

export const validateFamilyDetails = (employeeData) => {
  const { familyMembers, isOrphan } = employeeData || {};

  // Do not allow when flag is null/undefined – user must explicitly choose
  if (isOrphan === null || isOrphan === undefined) {
    return false;
  }

  const hasValidMember = Array.isArray(familyMembers)
    ? familyMembers.some((member) =>
        member?.name &&
        member?.relationship &&
        member?.gender &&
        (member?.dateOfBirth || member?.date_of_birth)
      )
    : false;

  if (isOrphan === true) {
    // Orphan: allow zero family records, but if any exist they must be valid
    return !familyMembers || familyMembers.length === 0 || hasValidMember;
  }

  // If isOrphan is false, require at least one complete family member
  if (!Array.isArray(familyMembers) || familyMembers.length === 0) {
    return false;
  }

  return hasValidMember;
};

export const validateBankDetails = (employeeData) => {
  if (!employeeData.bankName) return false;
  if (!employeeData.accountNumber) return false;
  if (!employeeData.ifscCode) return false;
  // accountType might not be in API response, so we'll skip it for validation
  if (!employeeData.bankBranch) return false;
  
  return true;
};

export const validateEducationalDetails = (employeeData) => {
  const { qualifications, lessThanPrimary } = employeeData || {};

  // Do not allow when flag is null/undefined – user must explicitly choose
  if (lessThanPrimary === null || lessThanPrimary === undefined) {
    return false;
  }

  const hasValidQualification = Array.isArray(qualifications)
    ? qualifications.some((qual) =>
        qual?.degree &&
        qual?.institution &&
        (qual?.yearOfPassing || qual?.year_of_passing) &&
        qual?.specialization
      )
    : false;

  if (lessThanPrimary === true) {
    // Less than primary: allow zero rows, but if any exist they must be valid
    return !qualifications || qualifications.length === 0 || hasValidQualification;
  }

  // If lessThanPrimary is false, require at least one complete qualification
  if (!Array.isArray(qualifications) || qualifications.length === 0) {
    return false;
  }

  return hasValidQualification;
};

export const validateProfessionalDetails = (employeeData) => {
  const { experiences, isFresher } = employeeData || {};

  // Do not allow when flag is null/undefined – user must explicitly choose
  if (isFresher === null || isFresher === undefined) {
    return false;
  }

  const hasValidExperience = Array.isArray(experiences)
    ? experiences.some((exp) =>
        (exp?.company || exp?.companyName) &&
        (exp?.position || exp?.designation) &&
        (exp?.fromDate || exp?.from_date) &&
        (exp?.toDate || exp?.to_date)
      )
    : false;

  if (isFresher === true) {
    // Fresher: allow zero rows, but if any exist they must be valid
    return !experiences || experiences.length === 0 || hasValidExperience;
  }

  // If isFresher is false, require at least one complete experience
  if (!Array.isArray(experiences) || experiences.length === 0) {
    return false;
  }

  return hasValidExperience;
};

// Data-presence checks for ESS tab availability: enable tab if section has data
// even when isOrphan/lessThanPrimary/isFresher are not set by API
export const hasFamilySectionData = (employeeData) => {
  const familyMembers = employeeData?.familyMembers;
  if (!Array.isArray(familyMembers) || familyMembers.length === 0) return false;
  return familyMembers.some(
    (member) =>
      member?.name &&
      member?.relationship &&
      member?.gender &&
      (member?.dateOfBirth ?? member?.date_of_birth)
  );
};

export const hasEducationalSectionData = (employeeData) => {
  const qualifications = employeeData?.qualifications;
  if (!Array.isArray(qualifications) || qualifications.length === 0) return false;
  return qualifications.some(
    (qual) =>
      qual?.degree &&
      qual?.institution &&
      (qual?.yearOfPassing ?? qual?.year_of_passing) != null &&
      qual?.specialization
  );
};

export const hasProfessionalSectionData = (employeeData) => {
  const experiences = employeeData?.experiences;
  if (!Array.isArray(experiences) || experiences.length === 0) return false;
  return experiences.some(
    (exp) =>
      (exp?.company ?? exp?.companyName) &&
      (exp?.position ?? exp?.designation) &&
      (exp?.fromDate ?? exp?.from_date) &&
      (exp?.toDate ?? exp?.to_date)
  );
};

export const validateStatutoryDetails = (employeeData) => {
  const toBool = (v) => {
    if (v === true || v === 1) return true;
    if (v === false || v === 0) return false;
    const s = String(v ?? "").trim().toLowerCase();
    if (s === "true" || s === "1" || s === "yes") return true;
    if (s === "false" || s === "0" || s === "no") return false;
    return null;
  };

  const pfApplicable = toBool(employeeData?.pfApplicable) === true;
  const esiApplicable = toBool(employeeData?.esiApplicable) === true;

  // If neither statutory applicability exists, treat as not filled
  const hasApplicability =
    employeeData?.pfApplicable !== undefined ||
    employeeData?.esiApplicable !== undefined ||
    employeeData?.ptApplicable !== undefined;
  if (!hasApplicability) return false;

  // PF rules (match StatutoryDetailsForm behavior)
  if (pfApplicable) {
    const hasUAN = toBool(employeeData?.hasUAN);
    if (hasUAN === null) return false;
    if (hasUAN !== true) return false; // UAN mandatory to proceed in this flow
    const uan = String(employeeData?.pfUANNumber ?? "").trim();
    if (!/^\d{12}$/.test(uan)) return false;

    const epsEligible = toBool(employeeData?.epsEligiblePreviousCompany);
    if (epsEligible === null) return false;
  }

  // ESI rules
  if (esiApplicable) {
    const hasESIIP = toBool(employeeData?.hasESIIP);
    if (hasESIIP === null) return false;

    if (!String(employeeData?.esiState ?? "").trim()) return false;
    if (!String(employeeData?.esiDistrict ?? "").trim()) return false;
    const dispensary = String(employeeData?.esiDispensary ?? "").trim();
    if (!dispensary) return false;
    if (!/^[A-Za-z\s]+$/.test(dispensary)) return false;

    // Only require IP number when user says they have one
    if (hasESIIP === true) {
      const ip = String(employeeData?.esiIPNumber ?? "").trim();
      if (!/^\d{10}$/.test(ip)) return false;
    }
  }

  return true;
};

export const validateDocuments = (employeeData) => {
  if (
    !employeeData.documents ||
    !Array.isArray(employeeData.documents) ||
    employeeData.documents.length === 0
  ) {
    return false;
  }

  // Only treat documents as valid if there is at least one
  // non-image document (i.e. not stored under family-images or personal-images)
  const NON_IMAGE_PREFIXES = ["family-images", "personal-images"];

  return employeeData.documents.some((doc) => {
    if (!doc || !doc.filePath) return false;

    const isImageOnlyPath = NON_IMAGE_PREFIXES.some((prefix) =>
      doc.filePath.includes(prefix)
    );

    if (isImageOnlyPath) {
      return false;
    }

    return (
      doc.documentName &&
      doc.originalFileName &&
      doc.filePath
    );
  });
};
